/**
 * Static Strategy Planner
 * 
 * Pre-race strategy optimizer. Given simple inputs (fuel capacity, avg lap time,
 * avg fuel per lap, race duration), evaluates ALL viable race plans and returns
 * the best one.
 * 
 * Strategy Types Evaluated:
 *   1. All full stops, full speed (no saving)
 *   2. All full stops + fuel saving (extend stints, potentially fewer stops)
 *   3. Mixed pit plan: (N-1) full stops + 1 splash (last stop is cheap)
 *   4. Mixed pit + mild saving
 *   5. Pure fuel saving (no stops, if possible)
 * 
 * Each plan is simulated stint-by-stint with the iRacing white flag rule to get
 * exact lap counts and total race time. Plans are ranked by total laps completed
 * (more laps = better), with total time as tiebreaker (faster = better position).
 * 
 * Pit Stop Rules (IMSA / iRacing):
 * - Full fuel (100%) = tires are FREE (fueling takes longer than tire change)
 * - Splash & go (partial fuel) = NO tires (too short for tire change)
 * - Full stop delta: ~55s (GTP/LMP2), ~62s (GT3)
 * - Splash delta: ~30s (GTP/LMP2), ~35s (GT3)
 */

import type { CarClass } from './liveStrategyCalculator';
import type { FuelSavingCalibration } from '../types/strategyCalibration';
import { buildFuelSavingModel } from './fuelSavingCalibration';

// ─── Input / Output Types ───────────────────────────────────────────────

export interface StaticStrategyInput {
  fuelCapacity: number;          // liters (tank size)
  avgFuelPerLap: number;         // liters per lap at normal pace
  avgLapTime: number;            // seconds per lap at normal pace
  raceDurationMinutes: number;   // race duration in minutes
  carClass?: CarClass;           // optional, for class-specific constants
  pitStopTime?: number;          // full stop delta override (seconds)
  pitStopSplashTime?: number;    // splash delta override (seconds)

  // Custom pit stop timing (from user input) — overrides class defaults when all 3 are set
  pitInTime?: number;            // seconds to enter pit lane
  pitOutTime?: number;           // seconds to exit pit lane
  fuelingTime?: number;          // seconds to refuel from empty to 100% (used to derive rate)
  fuelSavingCalibration?: FuelSavingCalibration;

  // Multi-class: lap time of the overall leader (e.g. GTP). When provided, the
  // effective race duration is computed from when the leader takes the checkered,
  // giving a correct lap count for slower classes.
  leaderLapTime?: number;

  // Minimum fuel to keep in tank before pitting (liters). Prevents engine stutter.
  // Hard floor of 0.3L enforced regardless of the value supplied.
  fuelSafetyMargin?: number;

  // Optional pace profiles for comparing different driving modes.
  // Each profile is evaluated independently to find the optimal stop count.
  paceProfiles?: PaceProfile[];
}

export interface PaceProfile {
  name: string;
  lapTime: number;       // avg lap time in seconds
  fuelPerLap: number;    // avg fuel consumption in liters
}

export interface PlanStint {
  stintNumber: number;
  laps: number;
  fuelAtStart: number;           // liters at stint start
  fuelAtEnd: number;             // liters at stint end
  fuelUsed: number;              // liters consumed
  stintTime: number;             // seconds of driving
  fuelPerLap: number;            // actual fuel rate this stint
  lapTime: number;               // actual lap time this stint
  pitAfter: boolean;             // pit stop follows this stint?
  pitType: 'full' | 'splash' | null;
  fuelAdded: number;             // liters added at pit (0 if no pit)
  pitTime: number;               // seconds lost at pit (0 if no pit)
}

export interface RacePlan {
  id: string;                    // unique identifier
  name: string;                  // short name (e.g. "5 stops full speed")
  description: string;           // human-readable summary
  category: 'full_speed' | 'fuel_save' | 'mixed_pit' | 'mixed_pit_save' | 'no_stop';
  isCalibrationReference?: boolean;
  totalLaps: number;             // laps completed (higher = better)
  totalRaceTime: number;         // total time including pits (seconds)
  totalDrivingTime: number;      // driving time only (seconds)
  totalPitTime: number;          // pit time only (seconds)
  totalFuelSaveTimeCost: number; // time lost from slower pace (seconds)
  fuelSavePercent: number;       // 0 if no saving
  adjustedFuelPerLap: number;    // actual fuel rate (after saving)
  adjustedLapTime: number;       // actual lap time (after saving)
  pitStops: number;              // number of pit stops
  maxStintLaps: number;          // longest stint in this plan
  pitPattern: 'all_full' | 'last_splash' | 'balanced';
  stints: PlanStint[];           // detailed stint breakdown
  timeDeltaVsBest: number;       // seconds behind best plan (0 = best)
  lapDeltaVsBest: number;        // laps behind best plan (0 = best)

  // Lap-count sensitivity window — the range of actual total laps over which
  // THIS plan beats all alternatives with a different stop count.
  // E.g. lapRangeLow=355 lapRangeHigh=362 means: "if you end up doing 355-362 laps,
  // this plan's stop count is optimal."  Outside that window a competitor plan wins.
  lapRangeLow: number | null;    // inclusive lower bound (null = no lower limit found)
  lapRangeHigh: number | null;   // inclusive upper bound (null = no upper limit found)
}

export interface StaticStrategyResult {
  bestPlan: RacePlan;
  allPlans: RacePlan[];
  input: StaticStrategyInput;
  summary: string;               // human-readable summary of the best plan
  stopCountTargets: StopCountTarget[];
  profileResults: ProfileResult[];
}

export interface ProfileResult {
  profile: PaceProfile;
  bestPlan: RacePlan | null;
  timeDeltaVsBest: number;
  lapDeltaVsBest: number;
}

export interface StopCountTarget {
  stops: number;
  maxFuelPerLap: number;
  stintLaps: number;
  totalPitTime: number;
  isBaseline: boolean;
  breakEvenLapTime: number | null;
  breakEvenDelta: number | null;
}

// ─── Planner Class ──────────────────────────────────────────────────────

export class StaticStrategyPlanner {

  private createEmptyPlan(input: StaticStrategyInput, description: string): RacePlan {
    return {
      id: 'empty',
      name: 'No Viable Strategy',
      description,
      category: 'full_speed',
      totalLaps: 0,
      totalRaceTime: 0,
      totalDrivingTime: 0,
      totalPitTime: 0,
      totalFuelSaveTimeCost: 0,
      fuelSavePercent: 0,
      adjustedFuelPerLap: input.avgFuelPerLap || 0,
      adjustedLapTime: input.avgLapTime || 0,
      pitStops: 0,
      maxStintLaps: 0,
      pitPattern: 'all_full',
      stints: [],
      timeDeltaVsBest: 0,
      lapDeltaVsBest: 0,
      lapRangeLow: null,
      lapRangeHigh: null,
    };
  }

  private formatSavePercent(value: number): string {
    return value.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
  }

  // ── Class-Specific Constants (same as live calculator) ────────────────

  private getMaxFuelSavePercent(carClass?: CarClass): number {
    switch (carClass) {
      case 'GTP':  return 15;
      case 'LMP2': return 12;
      case 'GT3':  return 15;
      default:     return 15;
    }
  }

  private getDefaultPitStopTime(carClass?: CarClass): number {
    switch (carClass) {
      case 'GTP':  return 55;
      case 'LMP2': return 55;
      case 'GT3':  return 62;
      default:     return 60;
    }
  }

  private getDefaultSplashTime(carClass?: CarClass): number {
    switch (carClass) {
      case 'GTP':  return 30;
      case 'LMP2': return 30;
      case 'GT3':  return 35;
      default:     return 32;
    }
  }

  private getFuelSaveLapTimePenalty(carClass?: CarClass): number {
    switch (carClass) {
      case 'GTP':  return 0.10;
      case 'LMP2': return 0.18;
      case 'GT3':  return 0.13;
      default:     return 0.15;
    }
  }

  // ── Main Entry Point ──────────────────────────────────────────────────

  /**
   * Plan the optimal race strategy.
   * 
   * Generates all feasible race plans (varying stop count, fuel save %, pit types)
   * and returns them ranked by total laps completed, then by total race time.
   * 
   * @param input - Simple race parameters
   * @returns All evaluated plans + the best one
   */
  planRace(input: StaticStrategyInput): StaticStrategyResult {
    // Validate
    if (!input.avgLapTime || !input.avgFuelPerLap || input.avgFuelPerLap <= 0 || !input.raceDurationMinutes) {
      const emptyPlan = this.createEmptyPlan(input, 'Provide valid inputs.');
      emptyPlan.name = 'Insufficient Data';
      return { bestPlan: emptyPlan, allPlans: [emptyPlan], input, summary: 'Insufficient data.', stopCountTargets: [], profileResults: [] };
    }

    const fullPitTime = input.pitStopTime ?? this.getDefaultPitStopTime(input.carClass);
    const splashPitTime = input.pitStopSplashTime ?? this.getDefaultSplashTime(input.carClass);
    const fuelSavingModel = buildFuelSavingModel({
      calibration: input.fuelSavingCalibration,
      defaultMaxFuelSavePercent: this.getMaxFuelSavePercent(input.carClass),
      defaultPenaltyPerPercent: this.getFuelSaveLapTimePenalty(input.carClass),
    });
    const maxFuelSave = fuelSavingModel.maxFuelSavePercent;
    const penaltyPerPercent = fuelSavingModel.lapTimePenaltyPerPercent;
    const observedSavePercent = fuelSavingModel.calibrationActive
      ? fuelSavingModel.observedSavePercent
      : null;
    const safetyMargin = Math.max(0.05, input.fuelSafetyMargin ?? 0.3);

    const maxStintLaps = Math.floor((input.fuelCapacity - safetyMargin) / input.avgFuelPerLap);
    const nominalRaceSeconds = input.raceDurationMinutes * 60;
    // If a leader lap time is provided (multi-class), the checkered falls when the
    // leader finishes their white-flag lap — which extends the effective window beyond
    // the nominal clock for slower classes.
    const raceSeconds = (input.leaderLapTime && input.leaderLapTime > 0)
      ? (Math.floor(nominalRaceSeconds / input.leaderLapTime) + 1) * input.leaderLapTime
      : nominalRaceSeconds;

    if (!input.fuelCapacity || input.fuelCapacity <= safetyMargin || maxStintLaps <= 0 || nominalRaceSeconds <= 0) {
      const emptyPlan = this.createEmptyPlan(input, 'No viable strategy with the current fuel capacity and race settings.');
      return {
        bestPlan: emptyPlan,
        allPlans: [emptyPlan],
        input,
        summary: 'No viable strategy with the current fuel capacity and race settings.',
        stopCountTargets: [],
        profileResults: [],
      };
    }

    // Rough estimate of laps without pits (for determining stop range)
    const roughLaps = Math.floor(raceSeconds / input.avgLapTime) + 1;
    const minStops = Math.max(0, Math.ceil(roughLaps / maxStintLaps) - 1);
    const maxStops = minStops + 2;

    const candidates: RacePlan[] = [];
    let planId = 0;

    // ── Helper: derive name & category from actual stint pit types ───────
    const labelPlan = (plan: RacePlan, save: number, pattern: 'all_full' | 'last_splash' | 'balanced', isCalibrationReference: boolean = false) => {
      plan.pitPattern = pattern;
      const actualStops = plan.pitStops;
      const splashCount = plan.stints.filter(s => s.pitType === 'splash').length;
      const fullCount = plan.stints.filter(s => s.pitType === 'full').length;
      const formattedSave = this.formatSavePercent(save);
      const saveStr = save > 0 ? ` + ${formattedSave}% save` : ', full speed';
      plan.isCalibrationReference = isCalibrationReference;

      if (actualStops === 0) {
        plan.name = save > 0 ? `No stops, save ${formattedSave}%` : 'No stops, full speed';
        plan.category = 'no_stop';
      } else if (splashCount > 0) {
        const pitLabel = fullCount > 0
          ? `${fullCount} full + ${splashCount} splash`
          : `${splashCount} splash`;
        plan.name = `${pitLabel}${saveStr}`;
        plan.category = save > 0 ? 'mixed_pit_save' : 'mixed_pit';
      } else {
        plan.name = `${actualStops} full stop(s)${saveStr}`;
        plan.category = save > 0 ? 'fuel_save' : 'full_speed';
      }

      if (isCalibrationReference) {
        plan.name = `${plan.name} (reference)`;
      }
    };

    // Generate candidates for each stop count
    for (let N = Math.max(0, minStops - 1); N <= maxStops; N++) {

      // ── Category 1: All full stops, full speed ────────────────────────
      const fullSpeedPlan = this.simulate(
        input, N, 0, 'all_full', fullPitTime, splashPitTime, safetyMargin, penaltyPerPercent
      );
      if (fullSpeedPlan) {
        fullSpeedPlan.id = `plan_${++planId}`;
        labelPlan(fullSpeedPlan, 0, 'all_full');
        candidates.push(fullSpeedPlan);
      }

      // ── Category 2: All full stops + fuel saving ──────────────────────
      // Try save % in steps of 1, from 1% up to class max
      for (let save = 1; save <= maxFuelSave; save += 1) {
        const plan = this.simulate(
          input, N, save, 'all_full', fullPitTime, splashPitTime, safetyMargin, penaltyPerPercent
        );
        if (plan) {
          plan.id = `plan_${++planId}`;
          labelPlan(plan, save, 'all_full');
          candidates.push(plan);
        }
      }

      if (observedSavePercent && observedSavePercent > 0) {
        const referencePlan = this.simulate(
          input, N, observedSavePercent, 'all_full', fullPitTime, splashPitTime, safetyMargin, penaltyPerPercent
        );
        if (referencePlan) {
          referencePlan.id = `plan_${++planId}`;
          labelPlan(referencePlan, observedSavePercent, 'all_full', true);
          candidates.push(referencePlan);
        }
      }

      if (N >= 1) {
        // ── Category 3: (N-1) full + 1 splash, no saving ───────────────
        const mixedPlan = this.simulate(
          input, N, 0, 'last_splash', fullPitTime, splashPitTime, safetyMargin, penaltyPerPercent
        );
        if (mixedPlan) {
          mixedPlan.id = `plan_${++planId}`;
          labelPlan(mixedPlan, 0, 'last_splash');
          candidates.push(mixedPlan);
        }

        // ── Category 4: (N-1) full + 1 splash + mild saving ────────────
        for (let save = 1; save <= Math.min(maxFuelSave, 10); save += 1) {
          const plan = this.simulate(
            input, N, save, 'last_splash', fullPitTime, splashPitTime, safetyMargin, penaltyPerPercent
          );
          if (plan) {
            plan.id = `plan_${++planId}`;
            labelPlan(plan, save, 'last_splash');
            candidates.push(plan);
          }
        }
      }

    }

    // Deduplicate: use actual full/splash counts + laps + save% as key
    const uniqueByKey = new Map<string, RacePlan>();
    for (const p of candidates) {
      const fullCount = p.stints.filter(s => s.pitType === 'full').length;
      const splashCount = p.stints.filter(s => s.pitType === 'splash').length;
      const key = `${p.totalLaps}_${fullCount}_${splashCount}_${p.fuelSavePercent}_${p.pitPattern}`;
      const existing = uniqueByKey.get(key);
      if (!existing || (p.isCalibrationReference && !existing.isCalibrationReference)) {
        uniqueByKey.set(key, p);
      }
    }
    const unique = Array.from(uniqueByKey.values());

    if (unique.length === 0) {
      const emptyPlan = this.createEmptyPlan(input, 'No viable strategy with the current fuel capacity and race settings.');
      return {
        bestPlan: emptyPlan,
        allPlans: [emptyPlan],
        input,
        summary: 'No viable strategy with the current fuel capacity and race settings.',
        stopCountTargets: [],
        profileResults: [],
      };
    }

    // Sort: most laps first, then lowest total time (faster finish = better position)
    unique.sort((a, b) => {
      if (b.totalLaps !== a.totalLaps) return b.totalLaps - a.totalLaps;
      return a.totalRaceTime - b.totalRaceTime;
    });

    // Calculate deltas vs best
    const best = unique[0];
    for (const plan of unique) {
      plan.lapDeltaVsBest = best.totalLaps - plan.totalLaps;
      plan.timeDeltaVsBest = plan.totalRaceTime - best.totalRaceTime;
    }

    // Compute lap-count sensitivity window for each plan.
    // For each stop-count group, find the crossover lap count where the adjacent
    // stop-count group's best plan becomes cheaper: projectedTime(plan, n) = n * adjLapTime + totalPitTime
    {
      const bestByStops = new Map<number, RacePlan>();
      for (const p of unique) {
        if (!bestByStops.has(p.pitStops)) bestByStops.set(p.pitStops, p);
      }
      const stopCounts = Array.from(bestByStops.keys()).sort((a, b) => a - b);

      // Returns the lap count where plan b overtakes plan a (a wins at fewer laps, b wins at more laps).
      const crossover = (a: RacePlan, b: RacePlan): number | null => {
        const d = a.adjustedLapTime - b.adjustedLapTime;
        if (Math.abs(d) < 0.001) return null;
        const n = (b.totalPitTime - a.totalPitTime) / d;
        return n > 0 ? Math.round(n) : null;
      };

      for (const plan of unique) {
        const idx = stopCounts.indexOf(plan.pitStops);
        if (idx > 0) {
          const fewer = bestByStops.get(stopCounts[idx - 1])!;
          plan.lapRangeLow = crossover(fewer, plan);
        }
        if (idx < stopCounts.length - 1) {
          const more = bestByStops.get(stopCounts[idx + 1])!;
          plan.lapRangeHigh = crossover(plan, more);
        }
      }
    }

    // Build summary
    const summary = this.buildSummary(best, unique, input);

    // ── Compute stop count targets and profile analysis ─────────────────
    const stopCountTargets = this.computeStopCountTargets(
      input, fullPitTime, safetyMargin, roughLaps, minStops
    );
    const profileResults = input.paceProfiles && input.paceProfiles.length > 0
      ? this.evaluateProfiles(input, fullPitTime, splashPitTime, safetyMargin, raceSeconds)
      : [];

    return {
      bestPlan: best,
      allPlans: unique,
      input,
      summary,
      stopCountTargets,
      profileResults,
    };
  }

  // ── Race Simulation Engine ────────────────────────────────────────────

  /**
   * Simulate a complete race with the given strategy parameters.
   * Uses the iRacing white flag rule: race ends when you start a lap
   * with less remaining time than your lap time (that's the white flag lap).
   * 
   * @returns A RacePlan if the strategy is physically feasible, or null if not
   */
  private simulate(
    input: StaticStrategyInput,
    targetStops: number,
    fuelSavePercent: number,
    pitPattern: 'all_full' | 'last_splash' | 'balanced',
    fullPitTime: number,
    splashPitTime: number,
    safetyMargin: number,
    penaltyPerPercent: number,
  ): RacePlan | null {
    const nominalRaceSeconds = input.raceDurationMinutes * 60;
    const raceSeconds = (input.leaderLapTime && input.leaderLapTime > 0)
      ? (Math.floor(nominalRaceSeconds / input.leaderLapTime) + 1) * input.leaderLapTime
      : nominalRaceSeconds;

    // Adjusted pace
    const adjustedFuelPerLap = input.avgFuelPerLap * (1 - fuelSavePercent / 100);
    const adjustedLapTime = input.avgLapTime + fuelSavePercent * penaltyPerPercent;

    if (adjustedFuelPerLap <= 0) return null;

    // Max laps per stint with adjusted fuel rate
    const usableFuel = input.fuelCapacity - safetyMargin;
    const maxStintLaps = Math.floor(usableFuel / adjustedFuelPerLap);
    if (maxStintLaps <= 0) return null;

    // Quick feasibility check (simulation will be accurate regardless)

    // Build stint plan
    const stints: PlanStint[] = [];
    let simTime = 0;
    let simLaps = 0;
    let currentFuel = input.fuelCapacity; // start with full tank
    let stopsUsed = 0;
    let stintNumber = 0;

    const finalizePlan = (
      simStints: PlanStint[],
      simLaps: number,
      simTimeValue: number,
      stopCount: number,
    ): RacePlan | null => {
      if (simTimeValue + 1e-6 < raceSeconds) {
        return null;
      }

      return this.buildPlan(
        simStints,
        simLaps,
        simTimeValue,
        fuelSavePercent,
        adjustedFuelPerLap,
        adjustedLapTime,
        stopCount,
        penaltyPerPercent,
        input,
      );
    };

    while (true) {
      stintNumber++;
      const fuelAtStintStart = currentFuel;
      let lapsInStint = 0;
      let stintDriveTime = 0;

      // Drive laps in this stint until fuel runs out or race ends
      while (true) {
        const fuelNeededForLap = adjustedFuelPerLap;

        // White flag rule: if remaining race time < one lap time, this is the final lap.
        // For the final lap we only need enough fuel to complete it — no safety reserve
        // needed since the race is over after the checkered flag.
        const remainingTime = raceSeconds - simTime;
        if (remainingTime < adjustedLapTime && simLaps > 0) {
          if (currentFuel >= fuelNeededForLap) {
            // White flag — complete this final lap and finish
            simTime += adjustedLapTime;
            currentFuel -= fuelNeededForLap;
            lapsInStint++;
            simLaps++;
            stintDriveTime += adjustedLapTime;
          }

          // Record this stint and we're done (whether or not we drove the white flag lap)
          stints.push({
            stintNumber, laps: lapsInStint,
            fuelAtStart: fuelAtStintStart, fuelAtEnd: currentFuel,
            fuelUsed: fuelAtStintStart - currentFuel, stintTime: stintDriveTime,
            fuelPerLap: adjustedFuelPerLap, lapTime: adjustedLapTime,
            pitAfter: false, pitType: null, fuelAdded: 0, pitTime: 0,
          });

          return finalizePlan(stints, simLaps, simTime, stopsUsed);
        }

        // Check fuel — need safetyMargin after the lap to keep driving in this stint
        if (currentFuel - fuelNeededForLap < safetyMargin && lapsInStint > 0) {
          // Not enough fuel for another lap — must pit (or race is over)
          break;
        }

        // Normal lap
        simTime += adjustedLapTime;
        currentFuel -= fuelNeededForLap;
        lapsInStint++;
        simLaps++;
        stintDriveTime += adjustedLapTime;
      }

      // Stint is over (fuel low). Do we pit?
      if (stopsUsed < targetStops) {
        const isTargetLastStop = stopsUsed === targetStops - 1;

        // ── Custom pit times: derive refuel rate from user inputs ──
        const hasCustomPit = input.pitInTime !== undefined && input.fuelingTime !== undefined && input.pitOutTime !== undefined;
        const refuelRate = hasCustomPit ? input.fuelingTime! / input.fuelCapacity : 0;

        // ── Detect effective last stop dynamically ──
        // If a full tank from this pit would last until the race ends, this IS
        // the last stop — regardless of how many stops the target says.
        const estPitTimeFull = hasCustomPit
          ? (input.pitInTime! + (input.fuelCapacity - currentFuel) * refuelRate + input.pitOutTime!)
          : fullPitTime;
        const remainingAfterFullPit = raceSeconds - simTime - estPitTimeFull;
        const lapsWithFullTank = Math.floor((input.fuelCapacity - safetyMargin) / adjustedFuelPerLap);
        const isEffectiveLastStop = isTargetLastStop || (remainingAfterFullPit / adjustedLapTime) <= lapsWithFullTank;

        const useSplash = pitPattern === 'last_splash' && isTargetLastStop;

        // ── Determine fuel to add ──
        let fuelToAdd: number;
        if (isEffectiveLastStop) {
          // LAST STOP: only add what's needed to finish the race.
          // Iterative solver — pit time depends on fuel added, which depends on
          // remaining time, which depends on pit time. Two iterations converge.
          let estFuel = input.fuelCapacity - currentFuel; // start with max
          for (let iter = 0; iter < 3; iter++) {
            const iterPitTime = hasCustomPit
              ? (input.pitInTime! + estFuel * refuelRate + input.pitOutTime!)
              : (useSplash ? splashPitTime : fullPitTime);
            const remainingRaceTime = raceSeconds - simTime - iterPitTime;
            const estRemainingLaps = Math.floor(remainingRaceTime / adjustedLapTime) + 1; // +1 = white flag lap
            const fuelNeeded = estRemainingLaps * adjustedFuelPerLap + safetyMargin - currentFuel;
            estFuel = Math.max(0, Math.min(fuelNeeded, input.fuelCapacity - currentFuel));
          }
          fuelToAdd = estFuel;

          // If pattern says splash but fuel exceeds 50% of tank, plan is invalid
          if (useSplash && fuelToAdd > input.fuelCapacity * 0.5) {
            return null;
          }
        } else {
          // NON-LAST STOP: fill to 100% (tires are free with full fuel)
          fuelToAdd = input.fuelCapacity - currentFuel;
        }

        // ── Calculate pit time ──
        // A "splash" is any fill under 50% of tank capacity — fueling too short for tire change.
        const isSmallFill = fuelToAdd < input.fuelCapacity * 0.5;
        let pitTime: number;
        if (hasCustomPit) {
          // Dynamic: actual refuel time scales with fuel added
          pitTime = input.pitInTime! + fuelToAdd * refuelRate + input.pitOutTime!;
        } else {
          pitTime = (useSplash || isSmallFill) ? splashPitTime : fullPitTime;
        }

        // Determine pit type label (full = tires free; splash = too short for tires)
        const pitTypeLabel: 'full' | 'splash' = (useSplash || isSmallFill) ? 'splash' : 'full';

        // Check there's time for the pit + at least one more lap
        if (simTime + pitTime + adjustedLapTime > raceSeconds + adjustedLapTime) {
          // No time for pit + lap — race effectively over
          stints.push({
            stintNumber, laps: lapsInStint,
            fuelAtStart: fuelAtStintStart, fuelAtEnd: currentFuel,
            fuelUsed: fuelAtStintStart - currentFuel, stintTime: stintDriveTime,
            fuelPerLap: adjustedFuelPerLap, lapTime: adjustedLapTime,
            pitAfter: false, pitType: null, fuelAdded: 0, pitTime: 0,
          });
          return finalizePlan(stints, simLaps, simTime, stopsUsed);
        }

        // Record stint + pit
        stints.push({
          stintNumber, laps: lapsInStint,
          fuelAtStart: fuelAtStintStart, fuelAtEnd: currentFuel,
          fuelUsed: fuelAtStintStart - currentFuel, stintTime: stintDriveTime,
          fuelPerLap: adjustedFuelPerLap, lapTime: adjustedLapTime,
          pitAfter: true, pitType: pitTypeLabel,
          fuelAdded: fuelToAdd, pitTime: pitTime,
        });

        simTime += pitTime;
        currentFuel += fuelToAdd;
        stopsUsed++;

        // After the effective last stop, no more pitting — drive to the finish
        if (isEffectiveLastStop) {
          stopsUsed = targetStops;
        }
      } else {
        // No more stops available before the timer expires. This plan is invalid.
        stints.push({
          stintNumber, laps: lapsInStint,
          fuelAtStart: fuelAtStintStart, fuelAtEnd: currentFuel,
          fuelUsed: fuelAtStintStart - currentFuel, stintTime: stintDriveTime,
          fuelPerLap: adjustedFuelPerLap, lapTime: adjustedLapTime,
          pitAfter: false, pitType: null, fuelAdded: 0, pitTime: 0,
        });
        return finalizePlan(stints, simLaps, simTime, stopsUsed);
      }

      // Safety: prevent infinite loop
      if (stintNumber > 50) break;
    }

    return null;
  }

  // ── Build Plan from Simulation Results ────────────────────────────────

  private buildPlan(
    stints: PlanStint[],
    totalLaps: number,
    totalRaceTime: number,
    fuelSavePercent: number,
    adjustedFuelPerLap: number,
    adjustedLapTime: number,
    _actualStopsFromCounter: number, // NOTE: may be inflated by isEffectiveLastStop — do NOT use
    penaltyPerPercent: number,
    _input: StaticStrategyInput,
  ): RacePlan {
    // Derive actual stop count from stints (ground truth, not the inflated counter)
    const actualStops = stints.filter(s => s.pitAfter).length;

    const totalPitTime = stints.reduce((sum, s) => sum + s.pitTime, 0);
    const totalDrivingTime = stints.reduce((sum, s) => sum + s.stintTime, 0);
    const maxStintLaps = Math.max(...stints.map(s => s.laps));
    const totalFuelSaveTimeCost = fuelSavePercent > 0
      ? fuelSavePercent * penaltyPerPercent * totalLaps
      : 0;
    const formattedSavePercent = this.formatSavePercent(fuelSavePercent);

    // Build description
    const saveStr = fuelSavePercent > 0
      ? `, saving ${formattedSavePercent}% fuel (${adjustedFuelPerLap.toFixed(2)}L/lap, +${(fuelSavePercent * penaltyPerPercent).toFixed(2)}s/lap)`
      : ', full speed';
    const splashCount = stints.filter(s => s.pitType === 'splash').length;
    const fullCount = stints.filter(s => s.pitType === 'full').length;
    const pitStr = actualStops === 0
      ? 'No pit stops'
      : splashCount > 0
        ? `${fullCount} full stop(s) + ${splashCount} splash`
        : `${actualStops} full stop(s)`;

    const description = `${pitStr}${saveStr}. ` +
      `${totalLaps} laps in ${this.formatTime(totalRaceTime)}. ` +
      `Pit time: ${totalPitTime.toFixed(0)}s. ` +
      (fuelSavePercent > 0 ? `Save penalty: ${totalFuelSaveTimeCost.toFixed(1)}s total.` : '');

    return {
      id: '', name: '', category: 'full_speed', // filled by caller
      totalLaps, totalRaceTime, totalDrivingTime, totalPitTime,
      totalFuelSaveTimeCost, fuelSavePercent,
      adjustedFuelPerLap, adjustedLapTime,
      pitStops: actualStops, maxStintLaps,
      pitPattern: 'all_full',  // overwritten by caller
      stints, description,
      timeDeltaVsBest: 0, lapDeltaVsBest: 0,
      lapRangeLow: null, lapRangeHigh: null,
    };
  }

  // ── Summary Builder ───────────────────────────────────────────────────

  private buildSummary(best: RacePlan, all: RacePlan[], input: StaticStrategyInput): string {
    const lines: string[] = [];
    const classLabel = input.carClass ?? 'Unknown';

    lines.push(`=== Best Strategy for ${classLabel} (${input.raceDurationMinutes}min race) ===`);
    lines.push(best.description);
    lines.push('');

    // Compare top 3
    const top = all.slice(0, Math.min(5, all.length));
    lines.push('Top strategies:');
    for (let i = 0; i < top.length; i++) {
      const p = top[i];
      const delta = i === 0 ? '(BEST)' : `(-${p.lapDeltaVsBest} lap${p.lapDeltaVsBest !== 1 ? 's' : ''}, +${p.timeDeltaVsBest.toFixed(1)}s)`;
      lines.push(`  ${i + 1}. ${p.name}: ${p.totalLaps} laps ${delta}`);
    }

    return lines.join('\n');
  }

  // ── Profile Analysis ──────────────────────────────────────────────────

  /**
   * Evaluate each pace profile to find its optimal pit strategy.
   * Returns profiles ranked by total laps (desc) then total time (asc).
   */
  private evaluateProfiles(
    input: StaticStrategyInput,
    fullPitTime: number,
    splashPitTime: number,
    safetyMargin: number,
    raceSeconds: number,
  ): ProfileResult[] {
    const allProfiles: PaceProfile[] = [
      { name: 'Full Speed', lapTime: input.avgLapTime, fuelPerLap: input.avgFuelPerLap },
      ...(input.paceProfiles ?? []),
    ];

    const usableFuel = input.fuelCapacity - safetyMargin;
    const results: ProfileResult[] = [];

    for (const profile of allProfiles) {
      if (profile.fuelPerLap <= 0 || profile.lapTime <= 0) {
        results.push({ profile, bestPlan: null, timeDeltaVsBest: 0, lapDeltaVsBest: 0 });
        continue;
      }

      const maxStintLaps = Math.floor(usableFuel / profile.fuelPerLap);
      if (maxStintLaps <= 0) {
        results.push({ profile, bestPlan: null, timeDeltaVsBest: 0, lapDeltaVsBest: 0 });
        continue;
      }

      const profileInput: StaticStrategyInput = {
        ...input,
        avgLapTime: profile.lapTime,
        avgFuelPerLap: profile.fuelPerLap,
        paceProfiles: undefined,
        fuelSavingCalibration: undefined,
      };

      const roughLaps = Math.floor(raceSeconds / profile.lapTime) + 1;
      const pMinStops = Math.max(0, Math.ceil(roughLaps / maxStintLaps) - 1);
      const pMaxStops = pMinStops + 2;

      let bestPlan: RacePlan | null = null;

      for (let N = Math.max(0, pMinStops - 1); N <= pMaxStops; N++) {
        const patterns: Array<'all_full' | 'last_splash'> = ['all_full'];
        if (N >= 1) patterns.push('last_splash');

        for (const pattern of patterns) {
          const plan = this.simulate(
            profileInput, N, 0, pattern,
            fullPitTime, splashPitTime, safetyMargin, 0
          );
          if (plan && (!bestPlan ||
            plan.totalLaps > bestPlan.totalLaps ||
            (plan.totalLaps === bestPlan.totalLaps && plan.totalRaceTime < bestPlan.totalRaceTime)
          )) {
            bestPlan = plan;
          }
        }
      }

      results.push({ profile, bestPlan, timeDeltaVsBest: 0, lapDeltaVsBest: 0 });
    }

    // Sort by laps desc, time asc
    results.sort((a, b) => {
      const aLaps = a.bestPlan?.totalLaps ?? 0;
      const bLaps = b.bestPlan?.totalLaps ?? 0;
      if (bLaps !== aLaps) return bLaps - aLaps;
      return (a.bestPlan?.totalRaceTime ?? Infinity) - (b.bestPlan?.totalRaceTime ?? Infinity);
    });

    const bestLaps = results[0]?.bestPlan?.totalLaps ?? 0;
    const bestTime = results[0]?.bestPlan?.totalRaceTime;
    for (const r of results) {
      r.lapDeltaVsBest = bestLaps - (r.bestPlan?.totalLaps ?? 0);
      r.timeDeltaVsBest = bestTime != null && r.bestPlan
        ? r.bestPlan.totalRaceTime - bestTime
        : 0;
    }

    return results;
  }

  /**
   * Compute per-stop-count targets: what fuel rate and lap time does each
   * stop count require? The break-even lap time shows how much slower the
   * driver can be (vs full speed) and still beat the next-higher stop count.
   */
  private computeStopCountTargets(
    input: StaticStrategyInput,
    fullPitTime: number,
    safetyMargin: number,
    roughLaps: number,
    baselineMinStops: number,
  ): StopCountTarget[] {
    const usableFuel = input.fuelCapacity - safetyMargin;
    if (usableFuel <= 0 || roughLaps <= 0) return [];

    const minN = Math.max(0, baselineMinStops - 2);
    const maxN = baselineMinStops + 2;
    const targets: StopCountTarget[] = [];

    for (let N = minN; N <= maxN; N++) {
      const stintLaps = Math.ceil(roughLaps / (N + 1));
      const maxFuelPerLap = usableFuel / stintLaps;
      const totalPitTime = N * fullPitTime;
      const isBaseline = N === baselineMinStops;

      // Break-even vs the full-speed baseline stop count:
      // Saving K stops gains K * fullPitTime seconds.
      // Cost = roughLaps * lapTimeDelta.
      // Break-even: lapTimeDelta = K * fullPitTime / roughLaps.
      const stopsSaved = baselineMinStops - N;
      let breakEvenLapTime: number | null = null;
      let breakEvenDelta: number | null = null;

      if (stopsSaved > 0) {
        breakEvenDelta = stopsSaved * fullPitTime / roughLaps;
        breakEvenLapTime = input.avgLapTime + breakEvenDelta;
      }

      targets.push({
        stops: N,
        maxFuelPerLap,
        stintLaps,
        totalPitTime,
        isBaseline,
        breakEvenLapTime,
        breakEvenDelta,
      });
    }

    return targets;
  }

  // ── Helpers ───────────────────────────────────────────────────────────

  private formatTime(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
}
